import type {
  LatLngBoundsExpression,
  LatLngTuple,
  Layer,
  LeafletEvent,
} from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { useAppearance } from '@backoffice-frontend/dark-mode'
import { useErrorHandlingQuery } from '@backoffice-frontend/data-fetching'
import {
  getHereApiKeyForStopNetworkMap,
  getHereRasterGreyTileUrl,
  getHereRasterNightTileUrl,
  getHereRasterTileUrl,
  getHereRasterTrafficTileUrl,
  getHereSatelliteTileUrl,
} from '@backoffice-frontend/here-maps-api'
import { useLocalStorage } from '@backoffice-frontend/hooks'
import { useServiceAreaByIdWithLocationAttributes } from '@backoffice-frontend/service-area-picker'
import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LayersControl, MapContainer, TileLayer } from 'react-leaflet'
import { useParams } from 'react-router'
import { useLeafletMapLineServiceConfigQuery } from '../../graphql/query/leafletMapLineServiceConfig.hook'
import { useReadableLayoutNames } from '../../utils/useReadableLayoutNames'
import { getServiceAreaMapCenter } from './MoiaMapUtils'
import { OverrideMapControlsCSS } from './OverrideMapControlsCSS'
import { RoutingAnchorsLayer } from './RoutingAnchorsLayer'

const DEFAULT_MAP_OVERLAY = {
  routingAnchors: 'routingAnchors',
  trafficConditions: 'trafficConditions',
}

export const DEFAULT_ZOOM = 12
const DEFAULT_CENTER: LatLngTuple = [53.594534, 9.99272]
const MAX_ZOOM = 19
const MIN_ZOOM = 3

const OSM_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
const stopNetworkApiKey = getHereApiKeyForStopNetworkMap()
const INITIAL_LAYER = getHereRasterTileUrl(stopNetworkApiKey)
const LAYOUT_TILES = {
  grey: getHereRasterGreyTileUrl(stopNetworkApiKey),
  normalNight: getHereRasterNightTileUrl(stopNetworkApiKey),
  satellite: getHereSatelliteTileUrl(stopNetworkApiKey),
  osm: OSM_TILE_URL,
}

type Props = {
  children: ReactNode
  className?: string
  center?: LatLngTuple
  layers?: Layer[] | React.JSX.Element[]
  serviceAreaId?: string
  preferCanvas?: boolean
  bounds?: LatLngBoundsExpression
  doubleClickZoom?: boolean
  options?: {
    enableTrafficConditions: boolean
    enableRoutingAnchors?: boolean
  }
  zoom?: number
  minZoom?: number
}

const useSetServiceAreaView = ({
  serviceAreaId,
  center,
  map,
  zoom,
}: Pick<Props, 'serviceAreaId' | 'center' | 'zoom'> & {
  map: L.Map | null
}) => {
  const { serviceAreaId: paramServiceAreaId } = useParams<{
    serviceAreaId: string
  }>()
  const serviceArea = useServiceAreaByIdWithLocationAttributes({
    serviceAreaId: paramServiceAreaId ?? serviceAreaId,
    fetchPolicy: 'no-cache',
  })

  // Track service-area changes with a pending-recenter ref. This decouples the
  // route-param change from the async no-cache fetch so the re-center fires
  // reliably even when the data resolves in a later render.
  const prevParamRef = useRef<string | undefined>(undefined)
  const pendingRecenterRef = useRef<string | null>(null)

  // Needed to deal with a change in zoom that was happening when user the tries to create a polygon or marker
  const prevZoomRef = useRef(zoom)
  useEffect(() => {
    if (!map) return

    let recenterTimeout: ReturnType<typeof setTimeout> | undefined

    if (prevParamRef.current === undefined) {
      // Fresh mount: only queue a re-center when no explicit center was given.
      // A defined center means the caller already has a position to restore.
      if (!center) {
        pendingRecenterRef.current = paramServiceAreaId ?? null
      }
    } else if (paramServiceAreaId !== prevParamRef.current) {
      // Service area changed while the map stayed mounted
      pendingRecenterRef.current = paramServiceAreaId ?? null
    }
    prevParamRef.current = paramServiceAreaId

    if (
      serviceArea?.id &&
      pendingRecenterRef.current &&
      serviceArea.id === pendingRecenterRef.current
    ) {
      pendingRecenterRef.current = null
      // Note(Filip): this way we are sure the zoom will properly be set even when map.getZoom() !== DEFAULT_ZOOM when service area is changed
      map.setZoom(DEFAULT_ZOOM)
      // Always use the new service area's geographic center — center prop may
      // still carry stale coordinates at this point.
      const target = getServiceAreaMapCenter(serviceArea) ?? DEFAULT_CENTER
      recenterTimeout = setTimeout(() => map.setView(target), 320)
    } else if (
      zoom &&
      zoom !== prevZoomRef.current &&
      !pendingRecenterRef.current
    ) {
      map.setZoom(zoom)
    }

    prevZoomRef.current = zoom

    // Clear the pending re-center on unmount/re-run so setView never fires on a
    // torn-down map (map.remove() nulls _mapPane, and setView reads _leaflet_pos
    // off it → "Cannot read properties of undefined (reading '_leaflet_pos')").
    return () => clearTimeout(recenterTimeout)
  }, [center, map, paramServiceAreaId, serviceArea, zoom])
}

const BaseMoiaMap = ({
  children,
  className,
  preferCanvas = false,
  serviceAreaId,
  layers = [],
  bounds,
  center,
  doubleClickZoom,
  options,
  zoom = DEFAULT_ZOOM,
  minZoom = MIN_ZOOM,
}: Props) => {
  const hereTrafficOverlayTileUrl =
    getHereRasterTrafficTileUrl(stopNetworkApiKey)
  const { t } = useTranslation()
  const { darkMode } = useAppearance()
  const [selectedLayer, setSelectedLayer] = useLocalStorage<string>(
    'hereMapSelectedLayer',
    INITIAL_LAYER,
  )
  const [map, setMap] = useState<L.Map | null>(null)
  const readableLayoutNames = useReadableLayoutNames()

  // Routing anchors are only relevant for service areas that don't run line
  // service, so gate the overlay on the config being explicitly disabled.
  const { data: lineServiceConfigData } = useErrorHandlingQuery(
    useLeafletMapLineServiceConfigQuery,
    {
      variables: { serviceAreaId: serviceAreaId ?? '' },
      skip: !serviceAreaId,
    },
  )
  const isLineServiceEnabled =
    lineServiceConfigData?.lineServiceConfig.enabled ?? false

  useSetServiceAreaView({
    serviceAreaId,
    map,
    zoom,
    center,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: Note(José): selectedLayer in stored on the local store, making the selection available when changing routes. Dark mode will be only checked on the initial render, and only from dark mode flag dependant
  useEffect(() => {
    if (darkMode) {
      setSelectedLayer(LAYOUT_TILES.normalNight)
    } else {
      setSelectedLayer(INITIAL_LAYER)
    }
  }, [darkMode])

  useEffect(() => {
    const handleBaseLayerChange = (e: LeafletEvent) => {
      // Note(Filip): Ignore the deprecated warning as it doesn't contain the propagatedFrom property.
      setSelectedLayer(e.layer._url)
    }

    if (map) {
      map.on('baselayerchange', handleBaseLayerChange)
    }

    return () => {
      map?.off('baselayerchange', handleBaseLayerChange)
    }
  }, [map, setSelectedLayer])

  return (
    <>
      <OverrideMapControlsCSS />
      <MapContainer
        css={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: '100%',
        }}
        className={className}
        preferCanvas={preferCanvas}
        center={center ?? DEFAULT_CENTER}
        zoom={zoom}
        maxZoom={MAX_ZOOM}
        minZoom={minZoom}
        bounds={bounds}
        doubleClickZoom={doubleClickZoom}
        inertia
        ref={setMap}
      >
        <LayersControl position="bottomleft">
          <>
            {/* Note(José): Having a separated initial layer will assure that the 'baselayerchange' event is always fired.
          See issue: https://gis.stackexchange.com/a/259909 */}
            <LayersControl.BaseLayer
              key="normal"
              name={readableLayoutNames['normal']}
              checked={INITIAL_LAYER === selectedLayer}
            >
              <TileLayer url={INITIAL_LAYER} maxNativeZoom={20} maxZoom={20} />
            </LayersControl.BaseLayer>
            {Object.entries(LAYOUT_TILES).map(entry => {
              const layoutKey = entry[0]
              const layout = entry[1]

              return (
                <LayersControl.BaseLayer
                  key={layoutKey}
                  name={readableLayoutNames[layoutKey]}
                  checked={layout === selectedLayer}
                >
                  <TileLayer url={layout} maxNativeZoom={20} maxZoom={20} />
                </LayersControl.BaseLayer>
              )
            })}

            {layers}

            {/* Only offer the routing anchors overlay when there is a service
            area to fetch anchor points for (otherwise the toggle would appear
            in service-area-less maps, e.g. demo routes, and do nothing) and
            line service is not enabled for that area. */}
            {serviceAreaId && !isLineServiceEnabled && (
              <LayersControl.Overlay
                key={DEFAULT_MAP_OVERLAY.routingAnchors}
                checked={options?.enableRoutingAnchors}
                name={t('leafletMap_options_routingAnchors_label')}
              >
                <RoutingAnchorsLayer
                  serviceAreaId={serviceAreaId}
                  overlayName={t('leafletMap_options_routingAnchors_label')}
                  initiallyActive={options?.enableRoutingAnchors}
                />
              </LayersControl.Overlay>
            )}

            <LayersControl.Overlay
              key={DEFAULT_MAP_OVERLAY.trafficConditions}
              checked={options?.enableTrafficConditions}
              name={t('leafletMap_options_trafficConditions_label')}
            >
              <TileLayer
                url={hereTrafficOverlayTileUrl}
                maxNativeZoom={20}
                maxZoom={20}
              />
            </LayersControl.Overlay>
          </>
        </LayersControl>

        {children}
      </MapContainer>
    </>
  )
}

export const MoiaMap = BaseMoiaMap
